"use client";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { useMotion } from "@/contexts/MotionContext";
import Image from "next/image";

const CLIENT_LOGOS = [
  { src: "/assets/logos/pentagram.svg", alt: "Pentagram" },
  { src: "/assets/logos/goodinside.svg", alt: "Good Inside" },
  { src: "/assets/logos/ff.svg", alt: "Feast & Fettle" },
  { src: "/assets/logos/splitwise.png", alt: "Splitwise", colorLogo: true },
  { src: "/assets/logos/ebay.svg", alt: "eBay" },
  { src: "/assets/logos/ametek.svg", alt: "Ametek" },
];

export default function ClientLogosSection() {
  const { isMotionEnabled } = useMotion();
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!isMotionEnabled) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % CLIENT_LOGOS.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [isMotionEnabled]);

  return (
    <section className="pb-16">
      <div className="container mx-auto px-4">
        <div className="relative overflow-hidden rounded-xl">
          {/* Textured background */}
          <div className="absolute inset-0 bg-card-texture bg-brand-yellow/5" />

          {/* Logo container */}
          <div className="relative px-4 md:px-8 py-8 md:py-16">
            {/* Mobile view - 2 column grid */}
            <div className="grid grid-cols-2 gap-4 md:hidden">
              {CLIENT_LOGOS.map((logo, index) => (
                <motion.div
                  key={logo.src}
                  initial={isMotionEnabled ? { opacity: 0, y: 20 } : undefined}
                  animate={
                    isMotionEnabled ? { opacity: 1, y: 0 } : { opacity: 1 }
                  }
                  transition={{
                    duration: 0.8,
                    delay: index * 0.1,
                  }}
                  className="flex items-center justify-center p-4 bg-white/5 backdrop-blur-sm rounded-lg"
                >
                  <Image
                    src={logo.src}
                    alt={logo.alt}
                    width={200}
                    height={48}
                    className={[
                      "w-full h-8 md:h-12 object-contain",
                      "colorLogo" in logo && logo.colorLogo
                        ? ""
                        : "filter dark:invert",
                    ].join(" ")}
                  />
                </motion.div>
              ))}
            </div>

            {/* Desktop view - Horizontal scroll */}
            <div className="hidden md:grid md:grid-cols-4 gap-8 relative h-32">
              {[0, 1, 2, 3].map((offset) => {
                const logoIndex = (currentIndex + offset) % CLIENT_LOGOS.length;
                const logo = CLIENT_LOGOS[logoIndex];

                return (
                  <motion.div
                    key={`${logoIndex}-${currentIndex}`}
                    initial={
                      isMotionEnabled ? { opacity: 0, y: 20 } : undefined
                    }
                    animate={
                      isMotionEnabled ? { opacity: 1, y: 0 } : { opacity: 1 }
                    }
                    transition={{
                      duration: 1.2,
                      delay: offset * 0.2,
                      ease: "easeInOut",
                    }}
                    className="flex items-center justify-center p-8 bg-white/5 backdrop-blur-sm rounded-lg"
                  >
                    <Image
                      src={logo.src}
                      alt={logo.alt}
                      width={200}
                      height={48}
                      className={[
                        "w-full h-12 object-contain",
                        "colorLogo" in logo && logo.colorLogo
                          ? ""
                          : "filter dark:invert",
                      ].join(" ")}
                    />
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
